<!-- 
화면명 : FList.vue
작성일 : 2026-08-17
설명   : 첨부파일 목록 및 뷰어(이미지/PDF) 팝업 화면
-->
<template>
  <div class="p-3 bg-slate-100 text-slate-800 min-h-screen select-none font-sans text-xs sm:text-sm flex flex-col h-screen">
    <!-- 상단 헤더 영역 -->
    <div class="flex justify-between items-center pb-2 flex-shrink-0 bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-2">
      <div class="flex items-center space-x-2">
        <span class="inline-block w-3.5 h-3.5 rounded-full bg-blue-600 border-2 border-blue-200"></span>
        <span class="font-bold text-base sm:text-lg text-slate-900">{{ titleText }}</span>
        <span v-if="currentGuid" class="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-mono">
          GUID: {{ currentGuid }}
        </span>
      </div>

      <!-- 상단 액션 버튼 그룹 -->
      <div class="flex items-center space-x-2">
        <button
          @click="fetchFileList"
          class="btn-action"
          title="새로고침"
        >
          <i class="fa fa-sync-alt mr-1 text-blue-600"></i> 새로고침
        </button>
        <button
          v-if="fileList.length > 0"
          @click="downloadAllFiles"
          class="btn-action"
          title="전체 다운로드"
        >
          <i class="fa fa-download mr-1 text-green-600"></i> 전체 다운로드
        </button>
        <button
          @click="btnClose"
          class="btn-action text-red-600 border-red-200 hover:bg-red-50"
        >
          <i class="fa fa-times mr-1 text-red-500"></i> 닫기
        </button>
      </div>
    </div>

    <!-- 본문 스플릿 뷰 영역 (좌측: 파일 그리드 목록, 우측: 파일 뷰어) -->
    <div class="flex-1 flex gap-3 min-h-0 overflow-hidden">
      <!-- 좌측: 파일 목록 패널 (40% 폭) -->
      <div class="w-5/12 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- 목록 헤더 -->
        <div class="p-2.5 bg-slate-50 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <div class="flex items-center space-x-1.5 font-bold text-slate-700">
            <i class="fa fa-folder-open text-blue-500"></i>
            <span>파일 목록</span>
            <span class="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full font-semibold">
              {{ fileList.length }}건
            </span>
          </div>
          <span class="text-xs text-gray-500">행을 클릭하여 미리보기</span>
        </div>

        <!-- 로딩 상태 -->
        <div v-if="isLoading" class="flex-1 flex flex-col items-center justify-center text-gray-500 space-y-2">
          <i class="fa fa-spinner fa-spin text-2xl text-blue-500"></i>
          <span>파일 목록을 불러오는 중...</span>
        </div>

        <!-- 파일 목록 테이블 -->
        <div v-else-if="fileList.length > 0" class="flex-1 overflow-y-auto">
          <table class="w-full border-collapse text-left">
            <thead class="bg-slate-100 sticky top-0 border-b border-gray-200 text-slate-600 text-xs select-none z-10">
              <tr>
                <th class="py-2 px-2 text-center font-bold w-12 border-r border-gray-200">No</th>
                <th class="py-2 px-3 font-bold border-r border-gray-200">파일명</th>
                <th class="py-2 px-2 text-center font-bold w-16 border-r border-gray-200">확장자</th>
                <th class="py-2 px-2 text-right font-bold w-20 border-r border-gray-200">크기</th>
                <th class="py-2 px-2 text-center font-bold w-16">다운</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 text-xs">
              <tr
                v-for="(item, index) in fileList"
                :key="item.FSEQ || index"
                @click="selectFile(item)"
                :class="[
                  'cursor-pointer transition-colors',
                  selectedFile && selectedFile.FSEQ === item.FSEQ
                    ? 'bg-blue-50/90 font-medium text-blue-900 border-l-4 border-blue-600'
                    : 'hover:bg-gray-50 text-slate-700'
                ]"
              >
                <td class="py-2 px-2 text-center text-gray-500 font-mono">
                  {{ item.FSEQ || (index + 1) }}
                </td>
                <td class="py-2 px-3 truncate max-w-xs" :title="item.FDISP">
                  <div class="flex items-center space-x-1.5">
                    <span class="text-base flex-shrink-0">
                      {{ getFileIcon(item.FEXT) }}
                    </span>
                    <span class="truncate">{{ item.FDISP }}</span>
                  </div>
                  <div v-if="item.INS_DATETIME" class="text-[10px] text-gray-400 mt-0.5">
                    {{ formatDateStr(item.INS_DATETIME) }} <span v-if="item.INS_USID">({{ item.INS_USID }})</span>
                  </div>
                </td>
                <td class="py-2 px-2 text-center font-mono uppercase text-gray-500">
                  {{ (item.FEXT || '').replace('.', '') }}
                </td>
                <td class="py-2 px-2 text-right text-gray-500">
                  {{ formatFileSize(item.FSIZE) }}
                </td>
                <td class="py-2 px-2 text-center" @click.stop>
                  <button
                    @click="downloadFile(item)"
                    class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                    title="다운로드"
                  >
                    <i class="fa fa-download"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 빈 데이터 상태 -->
        <div v-else class="flex-1 flex flex-col items-center justify-center text-gray-400 space-y-2 p-4">
          <i class="fa fa-folder-open text-4xl text-gray-300"></i>
          <span>첨부된 파일이 없습니다.</span>
        </div>
      </div>

      <!-- 우측: 파일 미리보기/뷰어 패널 (60% 폭) -->
      <div class="w-7/12 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- 뷰어 상단 정보바 및 컨트롤 -->
        <div class="p-2 bg-slate-50 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <div class="flex items-center space-x-2 min-w-0 pr-2">
            <span class="text-base flex-shrink-0">
              {{ selectedFile ? getFileIcon(selectedFile.FEXT) : '📄' }}
            </span>
            <span class="font-bold text-slate-800 truncate text-xs sm:text-sm" :title="selectedFile?.FDISP">
              {{ selectedFile ? selectedFile.FDISP : '선택된 파일 없음' }}
            </span>
            <span v-if="selectedFile" class="text-[11px] bg-gray-200 text-gray-700 px-1.5 py-0.2 rounded font-mono uppercase">
              {{ (selectedFile.FEXT || '').replace('.', '') }}
            </span>
          </div>

          <!-- 뷰어 컨트롤러 (이미지/공통) -->
          <div v-if="selectedFile" class="flex items-center space-x-1 flex-shrink-0">
            <!-- 이미지 전용 줌/회전 컨트롤 -->
            <template v-if="isImageFile(selectedFile.FEXT)">
              <button @click="zoomIn" class="btn-tool" title="확대 (+)">
                <i class="fa fa-search-plus"></i>
              </button>
              <button @click="zoomOut" class="btn-tool" title="축소 (-)">
                <i class="fa fa-search-minus"></i>
              </button>
              <button @click="resetZoom" class="btn-tool text-xs" title="100% 원본">
                {{ Math.round(zoomScale * 100) }}%
              </button>
              <button @click="rotateRight" class="btn-tool" title="90도 회전">
                <i class="fa fa-redo"></i>
              </button>
              <span class="h-4 w-px bg-gray-300 mx-1"></span>
            </template>

            <!-- 새 창으로 열기 & 다운로드 버튼 -->
            <button
              @click="openInNewTab"
              class="btn-tool"
              title="새 창에서 원본 열기"
            >
              <i class="fa fa-external-link-alt text-blue-600"></i>
            </button>
            <button
              @click="downloadFile(selectedFile)"
              class="btn-tool"
              title="다운로드"
            >
              <i class="fa fa-download text-green-600"></i>
            </button>
          </div>
        </div>

        <!-- 뷰어 본문 캔버스 영역 -->
        <div class="flex-1 bg-slate-900/5 relative overflow-hidden flex items-center justify-center p-2">
          <!-- 1. 이미지 뷰어 -->
          <div
            v-if="selectedFile && isImageFile(selectedFile.FEXT)"
            class="w-full h-full flex items-center justify-center overflow-auto checkerboard-bg rounded border border-gray-200"
          >
            <img
              :src="currentFileUrl"
              :alt="selectedFile.FDISP"
              :style="{
                transform: `scale(${zoomScale}) rotate(${rotationDeg}deg)`,
                transition: 'transform 0.15s ease-out',
                maxHeight: zoomScale === 1 ? '100%' : 'none',
                maxWidth: zoomScale === 1 ? '100%' : 'none',
              }"
              class="object-contain select-none shadow-md"
              @wheel.prevent="onImageWheel"
            />
          </div>

          <!-- 2. PDF 뷰어 -->
          <div
            v-else-if="selectedFile && isPdfFile(selectedFile.FEXT)"
            class="w-full h-full bg-white rounded border border-gray-200 overflow-hidden"
          >
            <iframe
              :src="currentFileUrl"
              class="w-full h-full border-0"
              title="PDF 뷰어"
            ></iframe>
          </div>

          <!-- 3. 기타 일반 파일 뷰어 카드 -->
          <div
            v-else-if="selectedFile"
            class="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-sm border border-gray-200 max-w-md text-center"
          >
            <div class="text-6xl mb-4 text-blue-500">
              <i class="fa fa-file-alt"></i>
            </div>
            <div class="font-bold text-slate-800 text-sm mb-1 break-all">
              {{ selectedFile.FDISP }}
            </div>
            <div class="text-xs text-gray-500 mb-4">
              {{ formatFileSize(selectedFile.FSIZE) }} • {{ (selectedFile.FEXT || '').toUpperCase() }} 파일
            </div>
            <p class="text-xs text-gray-400 mb-5">
              이 파일 형식은 브라우저 미리보기를 지원하지 않습니다. 다운로드하여 확인하세요.
            </p>
            <button
              @click="downloadFile(selectedFile)"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-xs shadow transition-colors flex items-center space-x-2"
            >
              <i class="fa fa-download"></i>
              <span>파일 다운로드</span>
            </button>
          </div>

          <!-- 4. 파일 미선택 상태 -->
          <div v-else class="flex flex-col items-center justify-center text-gray-400 space-y-2">
            <i class="fa fa-eye-slash text-4xl text-gray-300"></i>
            <span>좌측 목록에서 미리볼 파일을 선택하세요.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { saveAs } from "file-saver";
import { notify } from "@kyvg/vue3-notification";

const route = useRoute();

// 기본 파라미터 상태
const currentGuid = ref("");
const customTitle = ref("");
const companyId = ref("wy");

// 파일 목록 & 선택된 파일 상태
const fileList = ref([]);
const selectedFile = ref(null);
const isLoading = ref(false);

// 이미지 조작 상태
const zoomScale = ref(1);
const rotationDeg = ref(0);

// 타이틀 바 텍스트
const titleText = computed(() => {
  if (customTitle.value) return customTitle.value;
  return "첨부파일 목록 및 뷰어";
});

// 선택된 파일의 다운로드/미리보기 URL
const currentFileUrl = computed(() => {
  if (!selectedFile.value) return "";
  return getFileUrl(selectedFile.value);
});

// 파일 URL 생성 함수
function getFileUrl(file) {
  if (!file) return "";
  const cid = companyId.value || "wy";
  const guid = file.FGUID || currentGuid.value;
  const seq = file.FSEQ || 1;
  return `/fileapi/download/${cid}/${guid}/${seq}`;
}

// 확장자별 아이콘 매핑
function getFileIcon(ext = "") {
  const e = ext.toLowerCase().trim();
  if ([".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"].includes(e)) {
    return "🖼️";
  }
  if (e === ".pdf") {
    return "📄";
  }
  if ([".xls", ".xlsx", ".csv"].includes(e)) {
    return "📊";
  }
  if ([".doc", ".docx", ".hwp", ".txt"].includes(e)) {
    return "📝";
  }
  if ([".zip", ".rar", ".7z", ".tar", ".gz"].includes(e)) {
    return "🗜️";
  }
  return "📁";
}

// 이미지 파일 여부 판별
function isImageFile(ext = "") {
  const e = ext.toLowerCase().trim();
  return [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"].includes(e);
}

// PDF 파일 여부 판별
function isPdfFile(ext = "") {
  return ext.toLowerCase().trim() === ".pdf";
}

// 날짜 포맷 (YYYYMMDDHHmmss -> YYYY-MM-DD HH:mm)
function formatDateStr(str) {
  if (!str || str.length < 8) return str || "";
  const y = str.substring(0, 4);
  const m = str.substring(4, 6);
  const d = str.substring(6, 8);
  if (str.length >= 12) {
    const hh = str.substring(8, 10);
    const mm = str.substring(10, 12);
    return `${y}-${m}-${d} ${hh}:${mm}`;
  }
  return `${y}-${m}-${d}`;
}

// 파일 용량 포맷팅
function formatFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size === 0) return "-";
  if (size < 1024) return size + " B";
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
  return (size / (1024 * 1024)).toFixed(1) + " MB";
}

// 파일 선택 핸들러
function selectFile(file) {
  selectedFile.value = file;
  zoomScale.value = 1;
  rotationDeg.value = 0;
}

// 파일 목록 API 호출
async function fetchFileList() {
  if (!currentGuid.value || String(currentGuid.value).trim() === "") {
    fileList.value = [];
    selectedFile.value = null;
    return;
  }

  isLoading.value = true;
  const cid = companyId.value || "wy";
  const apiUrl = `/fileapi/select/${cid}/${currentGuid.value}`;

  try {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error(`서버 응답 오류 (HTTP ${response.status})`);
    }
    const data = await response.json();
    // console.log("FList fetchFileList data:", data);

    if (Array.isArray(data)) {
      fileList.value = data;
      if (data.length > 0) {
        // 첫 번째 파일 자동 선택
        selectFile(data[0]);
      } else {
        selectedFile.value = null;
      }
    } else {
      fileList.value = [];
      selectedFile.value = null;
    }
  } catch (err) {
    console.error("파일 목록 조회 오류:", err);
    notify({
      title: "조회 실패",
      text: "첨부파일 목록을 가져오는 데 실패했습니다.",
      type: "error",
    });
    fileList.value = [];
    selectedFile.value = null;
  } finally {
    isLoading.value = false;
  }
}

// 개별 파일 다운로드
async function downloadFile(file) {
  if (!file) return;
  try {
    const url = getFileUrl(file);
    const response = await fetch(url);
    const blob = await response.blob();
    saveAs(blob, file.FDISP || `file_${file.FSEQ}${file.FEXT || ''}`);
  } catch (err) {
    console.warn("Blob 다운로드 실패, 직접 링크로 대체:", err);
    window.open(getFileUrl(file), "_blank");
  }
}

// 전체 파일 일괄 다운로드
async function downloadAllFiles() {
  if (!fileList.value || fileList.value.length === 0) return;
  for (const file of fileList.value) {
    await downloadFile(file);
  }
}

// 새 창에서 원본 열기
function openInNewTab() {
  if (selectedFile.value) {
    window.open(currentFileUrl.value, "_blank");
  }
}

// 이미지 확대/축소 및 회전
function zoomIn() {
  if (zoomScale.value < 4) zoomScale.value = parseFloat((zoomScale.value + 0.25).toFixed(2));
}

function zoomOut() {
  if (zoomScale.value > 0.3) zoomScale.value = parseFloat((zoomScale.value - 0.25).toFixed(2));
}

function resetZoom() {
  zoomScale.value = 1;
  rotationDeg.value = 0;
}

function rotateRight() {
  rotationDeg.value = (rotationDeg.value + 90) % 360;
}

function onImageWheel(e) {
  if (e.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}

// 창 닫기
function btnClose() {
  window.close();
}

// 팝업 데이터 초기화 및 갱신
function initDataFromRoute() {
  if (route.query.guid) {
    currentGuid.value = String(route.query.guid).trim();
  }
  if (route.query.title) {
    customTitle.value = String(route.query.title).trim();
  }
  if (route.query.cid) {
    companyId.value = String(route.query.cid).trim();
  }

  if (currentGuid.value) {
    fetchFileList();
  }
}

// 부모 창으로부터 전달받는 postMessage 리스너
function handleMessage(event) {
  if (event.data && event.data.type === "UPDATE_FLIST_DATA") {
    if (event.data.guid) {
      currentGuid.value = String(event.data.guid).trim();
    }
    if (event.data.title) {
      customTitle.value = String(event.data.title).trim();
    }
    if (event.data.cid) {
      companyId.value = String(event.data.cid).trim();
    }
    fetchFileList();
  }
}

onMounted(() => {
  initDataFromRoute();
  window.addEventListener("message", handleMessage);
});

onUnmounted(() => {
  window.removeEventListener("message", handleMessage);
});

// 라우트 쿼리 변경 감지
watch(
  () => route.query.guid,
  (newGuid) => {
    if (newGuid && newGuid !== currentGuid.value) {
      initDataFromRoute();
    }
  }
);
</script>

<style scoped>
.btn-action {
  @apply px-3 py-1 bg-white border border-gray-300 rounded font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-xs flex items-center text-xs;
}

.btn-tool {
  @apply p-1.5 text-gray-600 hover:text-blue-600 hover:bg-gray-200 rounded transition-colors flex items-center justify-center text-xs;
}

.checkerboard-bg {
  background-color: #f8fafc;
  background-image: linear-gradient(45deg, #e2e8f0 25%, transparent 25%),
    linear-gradient(-45deg, #e2e8f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e2e8f0 75%),
    linear-gradient(-45deg, transparent 75%, #e2e8f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}
</style>
